import React, { useEffect, useRef } from "react";
import { CheckCircle2, MapPin } from "lucide-react";
import { SectionHeader } from "../../../components/ui/section";
import { cn } from "../../../lib/utils";
import type { ServiceAreaWithCoords } from "../../../../types/business";
import { CONTRACTOR_SURFACE_RADIUS_LG_CLASS } from "../layoutRadius";

type ServiceAreaSectionProps = {
  serviceAreas: ServiceAreaWithCoords[];
};

type GoogleMapsApi = {
  Map: new (
    el: HTMLElement,
    opts: object,
  ) => {
    setCenter: (c: object) => void;
    setZoom: (n: number) => void;
    fitBounds: (b: object, p?: number) => void;
  };
  LatLngBounds: new () => { extend: (loc: object) => void };
  Marker: new (opts: object) => object;
  Geocoder: new () => {
    geocode: (
      req: object,
      cb: (
        results: Array<{ geometry: { location: object } }> | null,
        status: string,
      ) => void,
    ) => void;
  };
};

declare global {
  interface Window {
    google?: { maps: GoogleMapsApi };
  }
}

const ServiceAreaMap: React.FC<{ areas: ServiceAreaWithCoords[] }> = ({
  areas,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<InstanceType<GoogleMapsApi["Map"]> | null>(
    null,
  );
  const [mapStatus, setMapStatus] = React.useState<
    "loading" | "ready" | "unavailable"
  >("loading");

  useEffect(() => {
    // Check if Google Maps is available (client-side only)
    const checkGoogleMaps = () => {
      if (window.google?.maps) {
        initializeMap();
      } else {
        // Google Maps not loaded yet - wait for it
        const timeout = setTimeout(() => {
          if (window.google?.maps) {
            initializeMap();
          } else {
            setMapStatus("unavailable");
          }
        }, 2000);
        return () => clearTimeout(timeout);
      }
    };

    const initializeMap = () => {
      const g = window.google;
      if (!mapRef.current || !g?.maps) {
        setMapStatus("unavailable");
        return;
      }

      const areasWithCoords = areas.filter(
        (a) => a.lat != null && a.lng != null,
      );

      // If we have coordinates, use them directly
      if (areasWithCoords.length > 0) {
        const avgLat =
          areasWithCoords.reduce((sum, a) => sum + (a.lat || 0), 0) /
          areasWithCoords.length;
        const avgLng =
          areasWithCoords.reduce((sum, a) => sum + (a.lng || 0), 0) /
          areasWithCoords.length;

        const map = new g.maps.Map(mapRef.current, {
          center: { lat: avgLat, lng: avgLng },
          zoom: 11,
          disableDefaultUI: true,
          zoomControl: true,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });

        mapInstanceRef.current = map;

        const bounds = new g.maps.LatLngBounds();
        areasWithCoords.forEach((area) => {
          if (area.lat && area.lng) {
            const position = { lat: area.lat, lng: area.lng };
            new g.maps.Marker({
              position,
              map,
              title: area.name,
            });
            bounds.extend(position);
          }
        });

        if (areasWithCoords.length > 1) {
          map.fitBounds(bounds, 50);
        }
        setMapStatus("ready");
        return;
      }

      // No stored coordinates - geocode the area names
      const geocoder = new g.maps.Geocoder();
      const map = new g.maps.Map(mapRef.current, {
        center: { lat: 43.65, lng: -79.38 },
        zoom: 10,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
        ],
      });
      mapInstanceRef.current = map;

      const bounds = new g.maps.LatLngBounds();
      let geocodedCount = 0;

      areas.forEach((area) => {
        geocoder.geocode(
          { address: area.name },
          (
            results: Array<{ geometry: { location: object } }> | null,
            status: string,
          ) => {
            geocodedCount++;
            if (status === "OK" && results && results[0]) {
              const location = results[0].geometry.location;
              new g.maps.Marker({
                position: location,
                map,
                title: area.name,
              });
              bounds.extend(location);

              if (geocodedCount === areas.length) {
                if (areas.length === 1) {
                  map.setCenter(location);
                  map.setZoom(11);
                } else {
                  map.fitBounds(bounds, 50);
                }
                setMapStatus("ready");
              }
            } else if (geocodedCount === areas.length) {
              setMapStatus("ready");
            }
          },
        );
      });
    };

    checkGoogleMaps();
  }, [areas]);

  // During SSR or while loading, show loading state
  if (mapStatus === "loading") {
    return (
      <div
        className={cn(
          "relative aspect-square w-full overflow-hidden border border-border",
          CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
        )}
      >
        <div ref={mapRef} className="h-full w-full" />
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
          <div className="text-sm text-muted-foreground">Loading map...</div>
        </div>
      </div>
    );
  }

  // Google Maps unavailable - show fallback
  if (mapStatus === "unavailable") {
    return (
      <div
        className={cn(
          "aspect-square overflow-hidden border border-border bg-muted/40",
          CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
        )}
      >
        <div className="flex h-full flex-col items-center justify-center p-8 text-center">
          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-accent/10">
            <MapPin className="h-10 w-10 text-accent" />
          </div>
          <h3 className="mb-2 font-display text-2xl font-semibold text-foreground">
            Local Coverage
          </h3>
          <p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground">
            Serving homes and businesses across your service radius.
          </p>
        </div>
      </div>
    );
  }

  // Map is ready
  return (
    <div
      className={cn(
        "relative aspect-square w-full overflow-hidden border border-border",
        CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
      )}
    >
      <div ref={mapRef} className="h-full w-full" />
    </div>
  );
};

export const ServiceAreaSection: React.FC<ServiceAreaSectionProps> = ({
  serviceAreas,
}) =>
  serviceAreas.length > 0 ? (
    <section id="service-area" className="bg-card py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeader
              kicker="Service Area"
              title={`Proudly Serving ${serviceAreas[0]?.name ?? "the local area"}`}
              description="We cover neighborhoods across your region and nearby communities."
              className="mb-8"
            />

            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {serviceAreas.map((area) => (
                <li key={area.name} className="flex items-center gap-2 py-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-accent" />
                  <span className="text-sm font-medium text-foreground">
                    {area.name}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-muted-foreground">
              Large projects outside the standard area may still be available.
            </p>
          </div>

          <div className="relative">
            <ServiceAreaMap areas={serviceAreas} />
          </div>
        </div>
      </div>
    </section>
  ) : null;
